import React, { useState, useEffect } from 'react';
import axios from "axios";
import { Route, Routes, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import StaffList from './components/staff/StaffList';
import StaffDetails from './components/staff/StaffDetails';
import StaffForm from './components/staff/StaffForm';
import StaffMigrationCenter from './pages/staff/StaffMigrationCenter';
import HistoricalStaffImport from './pages/staff/HistoricalStaffImport';
import AttendanceList from './components/AttendanceList';
import AttendanceImport from './pages/attendance/AttendanceImport';
import HotEventList from './components/HotEventList';
import HotEventDetail from './components/HotEventDetail';
import HotEventHistory from "./components/HotEventHistory";
import LocationsPage from "./pages/setup/LocationsPage";
import BeaconPage from './pages/setup/BeaconPage';
import ApprovalWorkflowPage from './pages/setup/ApprovalWorkflowPage';
import ExportT2HR from './pages/ExportT2HR';
import ApprovalCenter from './pages/ApprovalCenter';
import ManualRequestsReport from "./pages/reports/ManualRequestsReport";
import ShiftMaster from './pages/sm/ShiftMaster';
import ProjectManager from './pages/sm/ProjectManager';
import RouteMapManager from './pages/sm/RouteMapManager';
import HolidayCalendar from './pages/sm/HolidayCalendar';
import WorkPatterns from './pages/sm/WorkPatterns';
import StaffGroups from './pages/sm/StaffGroups';
import ExceptionManager from './pages/sm/ExceptionManager';
import RosterPlanner from './pages/sm/RosterPlanner';
import LatePolicyManager from './pages/sm/LatePolicyManager';
import AttendancePenaltyManager from './pages/sm/AttendancePenaltyManager';
import OTPolicyManager from './pages/sm/OTPolicyManager';
import AllowancePolicyManager from './pages/sm/AllowancePolicyManager';
import GroupRecurringRules from './pages/sm/GroupRecurringRules';
// import AllowancePolicyList from './pages/payroll/AllowancePolicyList';
// import AllowancePolicyEditor from './pages/payroll/AllowancePolicyEditor';
import AnnouncementList from './pages/announcements/AnnouncementList';
import AnnouncementEditor from './pages/announcements/AnnouncementEditor';
import OrgMaster from './pages/som/OrgMaster';
import JobGradeSetup from './pages/som/JobGradeSetup';
import LeaveManagement from './pages/leave/LeaveManagement';
import LeavePolicySettings from './pages/leave/LeavePolicySettings';
import LeaveBalances from './pages/leave/LeaveBalances';
import LeaveAdjustment from './pages/leave/LeaveAdjustment';
import SystemAccountsPage from './pages/setup/SystemAccountsPage';
import MonthlyReview from './pages/payroll/MonthlyReview';
import PayrollProcessing from './pages/payroll/PayrollProcessing';
import CalculationBases from './pages/payroll/CalculationBases';
import PayElements from './pages/payroll/PayElements';
import CostCentres from './pages/payroll/CostCentres';
import AccountingMap from './pages/payroll/AccountingMap';
import AccountExport from './pages/payroll/AccountExport';
import PayslipDistribution from './pages/payroll/PayslipDistribution';
import FinancialYears from './pages/payroll/FinancialYears';
import PayrollSettings from './pages/payroll/PayrollSettings';
import PayrollGroups from './pages/payroll/PayrollGroups';
import PendingPayrollSetups from './pages/payroll/PendingPayrollSetups';
import SecurityAccessPage from './pages/setup/SecurityAccessPage';
import BrandingSetupPage from './pages/setup/BrandingSetupPage';
import NewHirePortal from './pages/onboarding/NewHirePortal';
import OnboardingDashboard from './pages/onboarding/OnboardingDashboard';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useTenantContext } from './context/TenantContext';
import { useMemo } from 'react';

import PayrollDraftReview from './pages/payroll/PayrollDraftReview';
import OpeningBalances from './pages/payroll/OpeningBalances';
import AdhocVariablesList from './pages/payroll/AdhocVariablesList';
import AdhocVariablesImport from './pages/payroll/AdhocVariablesImport';
import ReportsDashboard from './pages/reports/ReportsDashboard';
import DocumentExpiryReport from './pages/reports/DocumentExpiryReport';
import DemographicAnalytics from './pages/reports/DemographicAnalytics';
import LateAnalytics from './pages/reports/LateAnalytics';
import LeaveSummaryReport from './pages/reports/LeaveSummaryReport';
import LeaveHistoryAuditReport from './pages/reports/LeaveHistoryAuditReport';
import AttendanceMatrixReport from './pages/reports/AttendanceMatrixReport';
import CustomPersonnelReport from './pages/reports/CustomPersonnelReport';
import StaffMovementReport from './pages/reports/StaffMovementReport';
import CessationTurnoverReport from './pages/reports/CessationTurnoverReport';
import DailyAttendanceSummary from './pages/reports/DailyAttendanceSummary';
import LeaveCalendar from './pages/reports/LeaveCalendar';
import LeaveAnalytics from './pages/reports/LeaveAnalytics';
import OTSubmissionAuditReport from './pages/reports/OTSubmissionAuditReport';
import EmployeeClockLogin from './pages/employeeClock/EmployeeClockLogin';
import EmployeeClockPage from './pages/employeeClock/EmployeeClockPage';





function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const { tenantProfile } = useTenantContext();

  useEffect(() => {
    // Set up global axios interceptor for 401s
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Skip for auth endpoints where 401 is expected (e.g., wrong password)
        const url = error.config?.url || '';
        const isAuthEndpoint = url.includes('/login') || 
                               url.includes('/web-login') ||
                               url.includes('/verify-device-otp') ||
                               url.includes('/request-otp');

        if (error.response?.status === 401 && !isAuthEndpoint) {
          console.warn("Global 401 Interceptor: Session expired or invalid token. Logging out.");
          sessionStorage.removeItem("user");
          sessionStorage.removeItem("token");
          sessionStorage.removeItem("session_valid");
          setIsLoggedIn(false);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      // Clean up interceptor on unmount
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const theme = useMemo(() => {
    const primaryColor = tenantProfile?.primary_color || '#4338ca';
    return createTheme({
      palette: {
        primary: {
          main: primaryColor,
        },
      },
    });
  }, [tenantProfile?.primary_color]);

  useEffect(() => {
    const checkSession = async () => {
      // First: check if we have a stored session token (set by Login.js)
      const storedToken = sessionStorage.getItem('token');
      const sessionValid = sessionStorage.getItem('session_valid');

      if (storedToken && sessionValid === 'yes') {
        // Try to validate with backend using Bearer token
        try {
          const tenant = localStorage.getItem("tenant") || "";
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/me`, {
            withCredentials: true,
            headers: {
              "X-Tenant": tenant,
              "Authorization": `Bearer ${storedToken}`
            }
          });
          if (res.data?.success && res.data?.user) {
            setIsLoggedIn(true);
            return;
          }
        } catch (err) {
          // Cookie check failed but we have a token — try cookie-only fallback
          console.warn("Bearer check failed, trying cookie:", err?.response?.status);
        }

        // Fallback: trust sessionStorage if token exists and not expired
        try {
          const { jwtDecode } = await import('jwt-decode');
          const decoded = jwtDecode(storedToken);
          if (decoded.exp * 1000 > Date.now()) {
            setIsLoggedIn(true);
            return;
          }
        } catch (e) {
          console.warn("JWT decode failed:", e);
        }
      }

      // No valid session found — redirect to login
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("session_valid");
      setIsLoggedIn(false);
    };

    const publicPath = window.location.pathname;
    if (
      publicPath !== "/login" &&
      !publicPath.startsWith("/web-clock") &&
      !publicPath.startsWith("/employee-clock")
    ) {
      checkSession();
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  if (isLoggedIn === null) {
    return <div>Loading...</div>; // Or any other loading indicator
  }

  return (
    <ThemeProvider theme={theme}>
      <Routes>
      <Route
        path="/web-clock/login"
        element={<EmployeeClockLogin />}
      />
      <Route
        path="/web-clock"
        element={<EmployeeClockPage />}
      />
      <Route
        path="/employee-clock/login"
        element={<EmployeeClockLogin />}
      />
      <Route
        path="/employee-clock"
        element={<EmployeeClockPage />}
      />
      <Route
        path="/onboard"
        element={<NewHirePortal />}
      />
      <Route
        path="/login"
        element={!isLoggedIn ? <Login setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/" />}
      />
      <Route
        path="/"
        element={isLoggedIn ? <Dashboard setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/staff"
        element={isLoggedIn ? <StaffList setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/staff/:id"
        element={isLoggedIn ? <StaffDetails setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/staff/edit/:id"
        element={isLoggedIn ? <StaffForm setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/staff/new"
        element={isLoggedIn ? <StaffForm setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/staff/rehire"
        element={isLoggedIn ? <StaffForm setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/staff/migrate"
        element={isLoggedIn ? <StaffMigrationCenter setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/staff/historical-import"
        element={isLoggedIn ? <HistoricalStaffImport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/staff/onboarding"
        element={isLoggedIn ? <OnboardingDashboard setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/attendance"
        element={isLoggedIn ? <AttendanceList setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/attendance/import"
        element={isLoggedIn ? <AttendanceImport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/adhoc/import"
        element={isLoggedIn ? <AdhocVariablesImport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/announcements"
        element={isLoggedIn ? <AnnouncementList setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/announcements/new"
        element={isLoggedIn ? <AnnouncementEditor setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/announcements/edit/:id"
        element={isLoggedIn ? <AnnouncementEditor setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/staff/list"
        element={isLoggedIn ? <StaffList setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/emergency/hot"
        element={isLoggedIn ? <HotEventList setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/emergency/hot/:eventId"
        element={
          isLoggedIn
            ? <HotEventDetail setIsLoggedIn={setIsLoggedIn} />
            : <Navigate to="/login" />
        }
      />
      <Route
        path="/emergency/hot/history"
        element={isLoggedIn ? <HotEventHistory setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route path="/setup/locations"
        element={isLoggedIn ? <LocationsPage setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route path="/setup/beacons"
        element={isLoggedIn ? <BeaconPage setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route path="/setup/workflow"
        element={isLoggedIn ? <ApprovalWorkflowPage setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/export/t2hr"
        element={isLoggedIn ? <ExportT2HR setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/approval/inbox"
        element={isLoggedIn ? <ApprovalCenter setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/reports"
        element={isLoggedIn ? <ReportsDashboard setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/manual-requests"
        element={isLoggedIn ? <ManualRequestsReport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/ot-submission-audit"
        element={isLoggedIn ? <OTSubmissionAuditReport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/document-expiry"
        element={isLoggedIn ? <DocumentExpiryReport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/demographics"
        element={isLoggedIn ? <DemographicAnalytics setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/late-analytics"
        element={isLoggedIn ? <LateAnalytics setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/leave-summary"
        element={isLoggedIn ? <LeaveSummaryReport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/leave-history"
        element={isLoggedIn ? <LeaveHistoryAuditReport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/attendance-matrix"
        element={isLoggedIn ? <AttendanceMatrixReport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/custom-personnel"
        element={isLoggedIn ? <CustomPersonnelReport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/staff-movement"
        element={isLoggedIn ? <StaffMovementReport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/turnover"
        element={isLoggedIn ? <CessationTurnoverReport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/daily-attendance"
        element={isLoggedIn ? <DailyAttendanceSummary setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/leave-calendar"
        element={isLoggedIn ? <LeaveCalendar setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/reports/leave-analytics"
        element={isLoggedIn ? <LeaveAnalytics setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />} />
      <Route
        path="/sm/shifts"
        element={isLoggedIn ? <ShiftMaster setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/sm/projects"
        element={isLoggedIn ? <ProjectManager setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/sm/route-map"
        element={isLoggedIn ? <RouteMapManager setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/sm/holidays"
        element={isLoggedIn ? <HolidayCalendar setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/sm/patterns"
        element={isLoggedIn ? <WorkPatterns setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/sm/groups"
        element={isLoggedIn ? <StaffGroups setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/sm/exceptions"
        element={isLoggedIn ? <ExceptionManager setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/sm/roster"
        element={isLoggedIn ? <RosterPlanner setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/sm/late-policies"
        element={isLoggedIn ? <LatePolicyManager setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/sm/attendance-penalty"
        element={isLoggedIn ? <AttendancePenaltyManager setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/sm/ot-policies"
        element={isLoggedIn ? <OTPolicyManager setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/sm/allowance-policies"
        element={isLoggedIn ? <AllowancePolicyManager setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/group-rules"
        element={isLoggedIn ? <GroupRecurringRules setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/leave/manage"
        element={isLoggedIn ? <LeaveManagement setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/leave/balances"
        element={isLoggedIn ? <LeaveBalances setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/leave/policies"
        element={isLoggedIn ? <LeavePolicySettings setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/leave/adjustment"
        element={isLoggedIn ? <LeaveAdjustment setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/som"
        element={isLoggedIn ? <OrgMaster setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/som/job-catalogue"
        element={isLoggedIn ? <JobGradeSetup setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/setup/system-accounts"
        element={isLoggedIn ? <SystemAccountsPage setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/setup/branding"
        element={isLoggedIn ? <BrandingSetupPage setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/setup/security"
        element={isLoggedIn ? <SecurityAccessPage setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/pending-setups"
        element={isLoggedIn ? <PendingPayrollSetups setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/monthly-review"
        element={isLoggedIn ? <MonthlyReview setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/execution"
        element={isLoggedIn ? <PayrollProcessing setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/calc-bases"
        element={isLoggedIn ? <CalculationBases setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/elements"
        element={isLoggedIn ? <PayElements setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/cost-centres"
        element={isLoggedIn ? <CostCentres setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/accounting-map"
        element={isLoggedIn ? <AccountingMap setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/payslip-distribution"
        element={isLoggedIn ? <PayslipDistribution setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/account-export"
        element={isLoggedIn ? <AccountExport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/financial-years"
        element={isLoggedIn ? <FinancialYears setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/settings"
        element={isLoggedIn ? <PayrollSettings setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/groups"
        element={isLoggedIn ? <PayrollGroups setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/draft-review"
        element={isLoggedIn ? <PayrollDraftReview setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/opening-balances"
        element={isLoggedIn ? <OpeningBalances setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/adhoc"
        element={isLoggedIn ? <AdhocVariablesList setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />
      <Route
        path="/payroll/adhoc-import"
        element={isLoggedIn ? <AdhocVariablesImport setIsLoggedIn={setIsLoggedIn} /> : <Navigate to="/login" />}
      />

      </Routes>
    </ThemeProvider>
  );
}

export default App;
